import { notify } from "@components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "@lib/storeManager";
import { hiddenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import axios from "axios";

const API_BASE_URL =
  import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? "http://localhost:8000/api";

export const axiosConfig = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();

  // Don't add Authorization header for refresh token requests
  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      hiddenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      const d = error.response.data;
      return notify.error(
        d?.message ?? d?.error?.message ?? d?.error ?? "Request failed"
      );
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshToken();
        const newAccessToken = response.access;
        setLocalAccessToken(newAccessToken);
        if (response.refresh) {
          setLocalRefreshToken(response.refresh);
        }
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      const d = error.response?.data;
      notify.error(
        d?.error?.message ?? d?.message ?? d?.error ?? "An error occurred"
      );
      return Promise.reject(error);
    }
  }
);

const refreshToken = async () => {
  const response = await axios.post(
    `${API_BASE_URL}/auth/token-refresh/`,
    new URLSearchParams({
      refresh: getRefreshToken() ?? "",
    }),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );
  return response.data;
};
